import React, { useState, useEffect } from "react";
import { DialogBox } from "@/components/common/DialogBox";
import AdtAlertsIcon from "@/assets/svg/adtAlerts.svg";
import SearchDialogBox from "@/components/common/SearchDialogBox";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { DataTable } from "@/components/common/Table";
import DialogDropdown from "@/components/common/DialogDropdown";
import { useDebounce } from "@/hooks/useDebounce";
import { columns } from "./adtAlertsDialogColumn";
import {
  applyTimeRangeSelection,
  normalizeListResponse,
} from "../common/dialogUtils";

interface AdtAlertsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
}

interface AdtAlert {
  id: number;
  eventdate: string;
  eventType: string;
  facility: string;
  diagnosis?: string;
  providerName?: string;
}

const AdtAlertsDialog: React.FC<AdtAlertsDialogProps> = ({
  open,
  onOpenChange,
  memberId,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [, setAlerts] = useState<AdtAlert[]>([]);
  const [allAlerts, setAllAlerts] = useState<AdtAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [, setPage] = useState(1);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const [timeRange, setTimeRange] = useState("all");
  const debouncedSearchValue = useDebounce(searchValue, 1000); // Debounce search value

  const rowsPerPage = 20;

  const timeRangeOptions = [
    { label: "All Time", value: "all" },
    { label: "1 Day", value: "1" },
    { label: "3 Days", value: "3" },
    { label: "7 Days", value: "7" },
    { label: "14 Days", value: "14" },
    { label: "30 Days", value: "30" },
    { label: "60 Days", value: "60" },
    { label: "90 Days", value: "90" },
    { label: "180 Days", value: "180" },
    { label: "365 Days", value: "365" },
  ];

  const handleTimeRangeChange = (data: {
    days?: string;
    value?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    applyTimeRangeSelection(data, setTimeRange, setStartDate, setEndDate);

  const fetchAlerts = async (
    pageNumber: number = 1,
    append: boolean = false
  ) => { // NOSONAR
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
        setAllAlerts([]);
        setPage(1);
        setHasMoreData(true);
      }

      const url = routes.cards.adtAlerts(memberId);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestBody: any = {
        view: "list",
        searchKey: debouncedSearchValue.trim() || undefined, // Use debounced value
        page: pageNumber,
        limit: rowsPerPage,
      };

      if (timeRange !== "all") {
        requestBody.days = timeRange;
        requestBody.startDate = startDate || undefined;
        requestBody.endDate = endDate || undefined;
      }

      const res = await api.post(url, requestBody);

      const rawData = normalizeListResponse<Record<string, unknown>>(res.data);

      const transformedData: AdtAlert[] = rawData.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any, index: number) => ({
          id: (pageNumber - 1) * rowsPerPage + index + 1,
          eventdate: item.eventDate || item.eventdate || item.date,
          eventType: item.eventType || item.event_type,
          facility: item.eventFacility || item.facilityName || item.facility,
          diagnoses: item.diagnoses || item.diagnosis,
          providerName:
            item.providerName ||
            item.provider_name ||
            item.provider ||
            item.eventProviderName,
        })
      );

      if (append) {
        setAllAlerts((prev) => [...prev, ...transformedData]);
      } else {
        setAllAlerts(transformedData);
        setAlerts(transformedData);
      }

      setHasMoreData(transformedData.length === rowsPerPage);

      setPage(pageNumber);
    } catch (err) {
      console.error("API Error fetching ADT alerts:", err);
      // const error = err as { response?: { data?: { message?: string } }; message?: string };
      // const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch ADT alerts.";
      // setError(errorMessage);
      setHasMoreData(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreData = async () => {
    if (!hasMoreData || isLoadingMore) return;
    const nextPage = Math.floor(allAlerts.length / rowsPerPage) + 1;
    await fetchAlerts(nextPage, true);
  };

  const handleScroll = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => {
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
    if (isNearBottom && hasMoreData && !isLoadingMore && !loading) {
      loadMoreData();
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setTimeRange("all");
      setStartDate(undefined);
      setEndDate(undefined);
      setAllAlerts([]);
      setPage(1);
      setHasMoreData(true);
    }
  }, [open]);

  // Combined useEffect - triggers on ANY filter change
  useEffect(() => {
    if (!open) return;
    fetchAlerts(1, false);
  }, [open, timeRange, startDate, endDate, debouncedSearchValue]);

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      icon={AdtAlertsIcon}
      iconAlt='ADT Alerts'
      title='ADT Alerts'>
      <div className='min-h-[500px]'>
        <div className='mb-4 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <SearchDialogBox
              value={searchValue}
              onChange={setSearchValue}
              placeholder='Search by Facility Name'
              width='368px'
            />

            <div style={{ width: "180px", height: "40px" }}>
              <DialogDropdown
                options={timeRangeOptions}
                value={timeRange}
                onChange={handleTimeRangeChange}
                icon='calendar-line'
              />
            </div>
          </div>

          <div className='overflow-x-auto sm:overflow-x-visible -mx-6 px-6 sm:mx-0 sm:px-0'>
            <div className='min-w-[800px] sm:min-w-0'>
              <div className='border rounded-lg'>
                <DataTable<AdtAlert>
                  data={{ data: allAlerts, total: allAlerts.length }}
                  columns={columns}
                  height={allAlerts.length === 0 ? "440px" : "auto"}
                  maxHeight={"440px"}
                  isLoading={loading}
                  isLoadingMore={isLoadingMore}
                  hasMoreData={hasMoreData}
                  allData={allAlerts}
                  onScroll={handleScroll}
                  skeletonCount={10}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogBox>
  );
};

export default AdtAlertsDialog;
